import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { EmptyState, PageTitle, Spinner } from "../../components/ui/misc";
import { Crumb, Page, PageBar } from "../../components/ui/PageBar";
import { Panel } from "../../components/ui/Panel";
import { plural } from "../../features/clients/client-ui";
import type { InternalDoc, Space } from "../../lib/api/models";
import { can, useMe } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import { useInternalDocuments, useSpaces } from "../../lib/queries";

/** How many documents "Recently changed" shows. */
const RECENT = 8;

const changedAt = (d: InternalDoc) => Date.parse(d.updatedAt) || 0;
/** The newest change first; documents changed at the same moment by title, so the order is the same on every load. */
const byNewest = (a: InternalDoc, b: InternalDoc) => changedAt(b) - changedAt(a) || a.title.localeCompare(b.title);

/** The latest changes across the topics. The document list does not say who made a change, so no such column. */
function RecentTable({ docs, topics }: { docs: InternalDoc[]; topics: Space[] }) {
  return (
    <Panel title="Recently changed">
      <div className="overflow-x-auto">
        <table aria-label="Recently changed" className="kd-datatable min-w-[640px]">
          <thead>
            <tr>
              <th>Code</th>
              <th>Title</th>
              <th>Topic</th>
              <th>Last changed</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((d) => {
              const topic = topics.find((s) => s.id === d.spaceId);
              return (
                <tr key={d.id}>
                  <td className="font-mono text-xs whitespace-nowrap text-ink">{d.code}</td>
                  <td>
                    <Link to="/d/$ref" params={{ ref: d.id }} className="font-semibold text-ink hover:text-green-ink">
                      {d.title}
                    </Link>
                  </td>
                  <td>
                    {topic ? (
                      <Link to="/topics/$slug" params={{ slug: topic.slug }} className="hover:text-green-ink">
                        {topic.name}
                      </Link>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap tabular-nums">{formatDate(d.updatedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/** One row per topic. `docs` is the Handbook's documents, newest first, or null while they are not there. */
function TopicsTable({ topics, docs }: { topics: Space[]; docs: InternalDoc[] | null }) {
  return (
    <Panel title="Topics">
      <div className="overflow-x-auto">
        <table aria-label="Topics" className="kd-datatable">
          <thead>
            <tr>
              <th>Topic</th>
              <th>Documents</th>
              <th>Last changed</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((s) => {
              const mine = docs?.filter((d) => d.spaceId === s.id);
              const newest = mine?.[0];
              return (
                <tr key={s.id}>
                  <td>
                    <span className="flex items-center gap-2">
                      <Link
                        to="/topics/$slug"
                        params={{ slug: s.slug }}
                        className="font-semibold text-ink hover:text-green-ink"
                      >
                        {s.name}
                      </Link>
                      {s.restricted ? (
                        <span className="inline-flex shrink-0 items-center text-ink-3">
                          <Lock size={12} aria-hidden="true" />
                          <span className="sr-only">Restricted</span>
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="whitespace-nowrap tabular-nums">
                    {mine ? plural(mine.length, "document", "documents") : null}
                  </td>
                  <td className="whitespace-nowrap tabular-nums">{newest ? formatDate(newest.updatedAt) : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/**
 * The Handbook's start page (handbook start spec §4.2): what changed lately, then the topics. A topic is an
 * internal space; topics are made under Admin → Topics, not here.
 */
export function HandbookPage() {
  const me = useMe();
  const { data: spaces, isLoading } = useSpaces();
  const { data: docs, isError } = useInternalDocuments();
  const topics = (spaces ?? []).filter((s) => s.kind === "internal");
  const newestFirst = docs ? [...docs].sort(byNewest) : null;
  const recent = (newestFirst ?? []).slice(0, RECENT);
  return (
    <>
      <PageBar>
        <Crumb current>Handbook</Crumb>
      </PageBar>
      <Page>
        <PageTitle>Handbook</PageTitle>
        {isLoading ? <Spinner label="Loading topics…" /> : null}
        {spaces && topics.length === 0 ? (
          <EmptyState
            title="No topics yet"
            body="A topic holds Klarity's own documents about one subject, for example Azure."
            action={
              can(me, "admin") ? (
                <Link to="/admin/topics" className="text-green-ink underline">
                  Create a topic
                </Link>
              ) : null
            }
          />
        ) : null}
        {topics.length > 0 ? (
          <div className="flex flex-col gap-6">
            {recent.length > 0 ? <RecentTable docs={recent} topics={topics} /> : null}
            <TopicsTable topics={topics} docs={newestFirst} />
            {isError && !docs ? <p className="text-sm text-danger">The documents could not be loaded.</p> : null}
          </div>
        ) : null}
      </Page>
    </>
  );
}
